import _ from 'lodash'
import React, { FC } from 'react'
import styled, { css } from 'styled-components'

import { Box } from '../../components/Box'
import { Button } from '../../components/Button'
import { Flex } from '../../components/Flex'
import { Image } from '../../components/Image'
import { Text, Title } from '../../components/Text'
import { brkmax, themeSpace } from '../../components/theme'

const Wrapper = styled.div`
  display: flex;
  border-radius: 4px;
  width: 100%;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.white};

  ${brkmax(
    0,
    css`
      margin-top: 40px;
      flex-direction: column;
    `
  )}
`
const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 26px;
  background-color: ${({ theme }) => theme.colors.primary};
  fill: red;

  ${brkmax(
    0,
    css`
      padding: 4px;
    `
  )}
`

const Row = styled(Box)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray};
  display: flex;

  ${brkmax(
    0,
    css`
      flex-direction: column;
    `
  )}
`
const ContentWrapper = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.gray};
  border-radius: 0 4px 4px 0;

  ${Row}:last-child {
    border-bottom: none;
  }

  margin: 0 ${-themeSpace(2)};
  ${brkmax(
    0,
    css`
      border-radius: 0 0px 4px 4px;
    `
  )};
`

type Action = {
  onClick: () => void
  text: string
  iconName: string
  disabled?: boolean
  loading?: boolean
}

type Cell = {
  label: string
  value: string
}

type InfoRow = {
  cellWidth?: number
  cells: Cell[]
  action?: Action
  content?: FC
}

export type ProfileInfoBoxProps = {
  image?: React.ReactNode
  rows: InfoRow[]
}

export const ProfileInfoBox = ({ image, rows }: ProfileInfoBoxProps) => {
  return (
    <Wrapper>
      <IconWrapper>{image}</IconWrapper>
      <ContentWrapper data-automation-id="accountingInfoBox">
        {_.map(rows, (r, i) => {
          const Content = r.content
          return (
            <Row pt={[2, 3]} pb={[2, 3]} key={i} px={2}>
              <Flex flexDirection="column" flex="1 1 auto">
                <Flex flexDirection={['column', 'row', 'row']} flexWrap="wrap" mx={-2}>
                  {_.map(r.cells, (c, ci) =>
                    !_.isNil(c.value) ? (
                      <Box
                        key={ci}
                        width={[1, 1, r.cellWidth || 1 / r.cells.length]}
                        pb={r.cellWidth ? '16px' : [2, 0, 0]}
                        px={2}
                      >
                        <Title level="small" text={c.label} bold padding={{ bottom: '8px' }} />
                        <Text>{c.value}</Text>
                      </Box>
                    ) : undefined
                  )}
                </Flex>
                {Content && <Content />}
              </Flex>
              {r.action && (
                <Flex flexShrink={0} alignItems="center" pt={[2, 0, 0]}>
                  <Button
                    block
                    variant="primary"
                    size="middle"
                    onClick={r.action.onClick}
                    icon={<Image type={r.action.iconName} alt={r.action.text} />}
                    iconPlacement="right"
                    disabled={r.action.disabled}
                    loading={r.action.loading}
                  >
                    {r.action.text}
                  </Button>
                </Flex>
              )}
            </Row>
          )
        })}
      </ContentWrapper>
    </Wrapper>
  )
}
